import { selectedAttachmentIds, intakePrefill } from './report-context';
import { z } from 'zod';
import { TICKET_TYPES } from './enums';
import { createTicketInput } from './schemas';

/**
 * External intake (JIRA-19): another platform's service account lets its own users report tickets through
 * Jira's creation screen in a popup. The service account is the reporter; the person is the external reporter.
 */
export const INTEGRATION_SOURCE_PATTERN = /^[a-z0-9-]{2,32}$/;
export const INTAKE_MESSAGE_TYPE = 'lixionary-jira:ticket-created';

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);

/**
 * The canonical origin of `value`, or null unless it is exactly an origin (scheme, host and optional port, no
 * path). Plain http is accepted only for local hosts and only when `allowLocalHttp` is set (development).
 */
export function normalizeOrigin(value: string, allowLocalHttp: boolean): string | null {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  if (url.origin === 'null' || `${url.origin}` !== value.replace(/\/$/, '')) return null;
  if (url.protocol === 'https:') return url.origin;
  if (url.protocol === 'http:' && allowLocalHttp && LOCAL_HOSTS.has(url.hostname)) return url.origin;
  return null;
}

export const integrationSettingsInput = z.object({
  source: z
    .string()
    .trim()
    .regex(INTEGRATION_SOURCE_PATTERN, 'Use 2-32 lowercase letters, digits or dashes, e.g. iam-v2'),
  intakeEnabled: z.boolean(),
  allowedOrigins: z.array(z.string().trim().min(1).max(200)).max(10),
  reviewOnCreate: z.boolean(),
  /** Omit when creating; required by the UI when changing existing settings. */
  expectedVersion: z.number().int().nonnegative().optional(),
});

export const externalReporterInput = z.object({
  externalId: z.string().trim().min(1, 'externalId is required').max(200),
  email: z.string().trim().toLowerCase().email().max(320).optional(),
  displayName: z.string().trim().min(1, 'displayName is required').max(120),
});

export const intakeLaunchInput = z.object({
  prefill: intakePrefill.optional(),
  attachmentIds: selectedAttachmentIds,
  projectKey: z.string(),
  type: z.enum(TICKET_TYPES).optional(),
  returnOrigin: z.string().max(200),
  reporter: externalReporterInput,
});

/** What an intake session may submit: no priority or parent (admin-only fields). */
export const intakeTicketInput = createTicketInput.omit({ priority: true, parentKey: true });
